import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { ZodError } from 'zod';
import { logEvent } from 'firebase/analytics';
import { FirebaseError } from 'firebase/app';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { firestore, loadAnalytics, storage, uploadFile } from '../../../lib';
import { Button } from '../../commons';
import { DropArea } from '../../commons/drag-and-drop';
import { Input, RadioCheckInput } from '../../inputs';
import { Application, Program, programSchema } from '../../../models';

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

interface ProgramFormProps {
  program: Program;
  application: Application;
  isReadOnly?: boolean;
}

export default function ProgramForm({
  application,
  program,
  isReadOnly = false,
}: ProgramFormProps) {
  const router = useRouter();
  const [schoolFile, setSchoolFile] = React.useState<File | null>(null);
  const [transcriptFile, setTranscriptFile] = React.useState<File | null>(null);
  const onDropSchool = React.useCallback((acceptedFiles: File[]): void => {
    const [_file] = acceptedFiles;
    if (_file.size > 10000000) {
      return console.error('Mas 10MB');
    }
    setSchoolFile(_file);
  }, []);
  const onDropTranscript = React.useCallback((acceptedFiles: File[]): void => {
    const [_file] = acceptedFiles;
    if (_file.size > 10000000) {
      return console.error('Mas 10MB');
    }
    setTranscriptFile(_file);
  }, []);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    let _program: Partial<Program> | null = null;
    try {
      _program = programSchema.parse(data);
    } catch (err) {
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E51'
      );
    }
    if (!program?.urlSchoolID && !schoolFile) {
      return toastError('You must upload your school ID');
    }
    if (!program?.academicTranscript && !transcriptFile) {
      return toastError('You must upload your academic transcript');
    }
    NProgress.start();
    let urlSchoolID = '';
    let academicTranscript = '';
    try {
      if (schoolFile) {
        urlSchoolID = await uploadFile(
          storage,
          schoolFile,
          `/applications/${application?.id}/school-ID`
        );
      } else {
        urlSchoolID = program?.urlSchoolID;
      }
    } catch (err) {
      NProgress.done();
      return toastError(
        'Error uploading school ID file, check your internet. CODE: E52'
      );
    }
    try {
      if (transcriptFile) {
        academicTranscript = await uploadFile(
          storage,
          transcriptFile,
          `/applications/${application?.id}/academic-transcript`
        );
      } else {
        academicTranscript = program?.academicTranscript;
      }
    } catch (err) {
      NProgress.done();
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'storage/quota-exceeded':
            return toastError(
              'Error uploading transcript file, check your internet. Please contact support with this CODE: storage/quota-exceeded'
            );
          case 'storage/retry-limit-exceeded':
          case 'storage/invalid-checksum':
          case 'storage/canceled':
          case 'storage/invalid-argument':
            return toastError(
              'Error uploading transcript file, your internet is unstable. FE53.1'
            );
          default:
            return toastError(
              'Error uploading transcript file, check your internet. CODE: FE53'
            );
        }
      }
      return toastError(
        'Error uploading transcript file, check your internet. CODE: E53'
      );
    }
    try {
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '5',
        program: {
          ..._program,
          urlSchoolID,
          academicTranscript,
        },
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '5',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '5',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/6'
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-5' });
      Sentry.captureException(err);
      return toastError(
        'Error updating your application, check your internet. CODE: E54'
      );
    }
  };
  if (!application) {
    return null;
  }
  return (
    <form method="POST" onSubmit={onSubmit} className="w-full flex flex-col">
      <Input
        type="text"
        label="Name and location of the institute you are/or will be attending"
        name="institute"
        defaultValue={program?.institute ?? ''}
        placeholder="Name and location of the institute you are/or will be attending"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <Input
        type="text"
        label="Course of study (major)"
        name="major"
        defaultValue={program?.major ?? ''}
        placeholder="Course of study (major)"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <Input
        type="text"
        label="Duration of Study"
        name="duration"
        defaultValue={program?.duration ?? ''}
        placeholder="Duration of Study"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <p className="text-gray-500 text-[14px] font-bold mb-2">
        What is your academic status at the time of this application?
      </p>
      <RadioCheckInput
        type="radio"
        label="I will be an incoming freshman graduating high school"
        name="seniority"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        value="freshman"
        defaultChecked={program?.seniority === 'freshman'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        type="radio"
        label="I will be entering my second year of study"
        name="seniority"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        value="sophomore"
        defaultChecked={program?.seniority === 'sophomore'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        type="radio"
        label="I will be entering my third year of study"
        name="seniority"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        value="junior"
        defaultChecked={program?.seniority === 'junior'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        type="radio"
        label="I will be entering my fourth year of study"
        name="seniority"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        value="senior"
        defaultChecked={program?.seniority === 'senior'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        type="radio"
        label="I will be entering my final year of study"
        name="seniority"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        value="last"
        defaultChecked={program?.seniority === 'last'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        label="Please indicate the cost of tuition for this year"
        name="tuition"
        defaultValue={program?.tuition ?? ''}
        placeholder="Please indicate the cost of tuition for this year"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <Input
        type="number"
        min="0"
        step="0.01"
        label="Please indicate your most recent cumulative GPA"
        name="gpa"
        defaultValue={program?.gpa ?? ''}
        placeholder="Please indicate your most recent cumulative GPA"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      {isReadOnly ? null : (
        <DropArea
          label={
            schoolFile
              ? schoolFile.name
              : 'Kindly upload copy of your school ID'
          }
          document="school ID"
          override={!!schoolFile}
          onDrop={onDropSchool}
          accept={{
            // 'image/*': ['.jpeg', '.jpg', '.png'],
            'application/pdf': ['.pdf'],
          }}
        />
      )}
      {program?.urlSchoolID ? (
        <a
          href={program?.urlSchoolID}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] text-neutral-400 mb-8"
        >
          Preview File (School ID)
        </a>
      ) : null}
      {isReadOnly ? null : (
        <DropArea
          label={
            transcriptFile
              ? transcriptFile.name
              : 'Kindly upload your official academic transcript giving subject details and results'
          }
          document="academic transcript"
          override={!!transcriptFile}
          onDrop={onDropTranscript}
          accept={{
            // 'image/*': ['.jpeg', '.jpg', '.png'],
            'application/pdf': ['.pdf'],
          }}
        />
      )}
      {program?.academicTranscript ? (
        <a
          href={program?.academicTranscript}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[13px] text-neutral-400 mb-8"
        >
          Preview File (Academic Transcript)
        </a>
      ) : null}
      <p className="text-gray-500 text-[14px] font-bold mt-4 mb-2">
        Have you previously been awarded any scholarship, bursary, etc, in
        connection to your present course of study?
      </p>
      <RadioCheckInput
        type="radio"
        label="Yes"
        name="previousScholarship"
        value="yes"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        defaultChecked={program?.previousScholarship === 'yes'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <RadioCheckInput
        type="radio"
        label="No"
        name="previousScholarship"
        value="no"
        labelClassName="font-bold text-[20px] text-neutral-500"
        className="mb-2"
        defaultChecked={program?.previousScholarship === 'no'}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <Input
        type="text"
        label="If yes, please supply details"
        name="details"
        defaultValue={program?.details ?? ''}
        placeholder="Details"
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
